import { formatMessageTime, formatRelativeTime } from '@/lib/helpers';
import { mapClassificationToLeadStatusWithDefault } from '@/lib/omnichannel/classification';
import {
  mapApiMessageToUi,
  previewLabelFromAttachments,
} from '@/lib/helpers/omnichannel-media';
import type {
  ChannelType,
  Conversation,
  Message,
  OmnichannelConversation,
  OmnichannelConversationItem,
  OmnichannelMessage,
} from '@/types';

export const getInitials = (name: string): string => {
  const words = name.split(' ');
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

function isNewConversationShape(
  c: OmnichannelConversation
): c is OmnichannelConversationItem {
  return 'participant' in c && c.participant != null;
}

export const transformConversation = (
  apiConversation: OmnichannelConversation | null | undefined,
  channel: ChannelType,
  _businessId?: string
): Conversation => {
  if (!apiConversation) {
    return {
      id: '',
      name: 'Unknown User',
      initials: 'UU',
      lastMessage: '(No message)',
      time: 'Just now',
      channel,
      status: 'cold',
      unread: 0,
      avatar: null,
    };
  }

  if (isNewConversationShape(apiConversation)) {
    const {
      participant,
      lastMessage,
      unreadCount,
      responseWindow,
      classification,
    } = apiConversation;
    const name = participant?.name || 'Unknown User';
    const lm = (lastMessage || {}) as {
      message?: string;
      timestamp?: string;
      hasAttachment?: boolean;
      attachments?: unknown;
    };
    const attachmentLabel = lm.attachments
      ? previewLabelFromAttachments(lm.attachments)
      : null;
    const lastMessageText =
      lm.message ??
      attachmentLabel ??
      (lm.hasAttachment ? '[Attachment]' : '(No message)');
    return {
      id: apiConversation.id || '',
      name,
      initials: getInitials(name),
      lastMessage: lastMessageText,
      time: formatMessageTime(lm.timestamp),
      lastMessageAt: lm.timestamp || undefined,
      channel,
      status: mapClassificationToLeadStatusWithDefault(classification),
      unread: unreadCount ?? 0,
      avatar: participant?.picture ?? null,
      responseWindow: responseWindow
        ? {
            expiresAt: responseWindow.expiresAt ?? null,
            extendedWindowExpiresAt:
              responseWindow.extendedWindowExpiresAt ?? null,
            isExpired: !!responseWindow.isExpired,
            canSendFreeForm: !!responseWindow.canSendFreeForm,
          }
        : undefined,
    };
  }

  const senders = apiConversation.senders?.data || [];
  const customer = senders.find((s) => s.id !== _businessId) || senders[0];
  const messages = apiConversation.messages?.data || [];
  const newestMsg = messages.reduce((acc, cur) => {
    const accT = acc?.created_time ? new Date(acc.created_time).getTime() : 0;
    const curT = cur?.created_time ? new Date(cur.created_time).getTime() : 0;
    return curT >= accT ? cur : acc;
  }, messages[0]);
  const newestAttachmentLabel = newestMsg?.attachments
    ? previewLabelFromAttachments(newestMsg.attachments)
    : null;
  const lastMessage = newestMsg?.message || newestAttachmentLabel || '';
  const lastMessageTime =
    newestMsg?.created_time || apiConversation.updated_time;

  return {
    id: apiConversation.id || '',
    name: customer?.name || 'Unknown User',
    initials: getInitials(customer?.name || 'Unknown User'),
    lastMessage: lastMessage || '(No message)',
    time: formatRelativeTime(lastMessageTime),
    lastMessageAt:
      typeof lastMessageTime === 'string' ? lastMessageTime : undefined,
    channel,
    status: 'cold',
    unread: 0,
    avatar: null,
  };
};

export const transformMessages = (
  apiMessages: OmnichannelMessage[],
  channel: ChannelType,
  conversationId: string,
  businessId?: string,
  initials?: { customerInitials?: string; businessInitials?: string }
): Message[] => {
  return apiMessages
    .map((msg) =>
      mapApiMessageToUi(msg, channel, conversationId, businessId, initials)
    )
    .sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return ta - tb;
    });
};
